// Full-page weight log (route /progress/weight-history) — replaces the old
// "all entries" bottom sheet (owner: history deserves a page, not a modal).
// Reads the same cached resource as the Progress page, so it opens instantly;
// rows edit through the same measurement sheet Progress uses.
import { useState } from "react";
import { api } from "../lib/api";
import { useCachedResource } from "../lib/useCachedResource";
import { useT } from "../lib/i18n";
import { useUnits } from "../lib/useUnits";
import { addDays, todayLocal, relativeDayLabel } from "../lib/date";
import { PageHeader } from "../components/ui/PageHeader";
import { ErrorState } from "../components/ui/ErrorState";
import { Skeleton } from "../components/ui/Skeleton";
import { EditSheet, type FieldSpec } from "../components/EditSheet";
import type { Measurement, Goals } from "@shared/types";

const WEIGHT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function WeightHistory() {
  const t = useT();
  const units = useUnits();
  const [editing, setEditing] = useState<Measurement | null>(null);

  // The measurement editor stores canonical kg/cm (power-user sheet; labels
  // say so honestly) — identical to the editor on the Progress page.
  const MEAS_FIELDS: FieldSpec[] = [
    { key: "weight_kg",   label: "Weight (kg)",        type: "number", step: 0.1 },
    { key: "waist_cm",    label: "Waist (cm)",         type: "number", step: 0.1 },
    { key: "steps",       label: "Steps",              type: "number" },
    { key: "sleep_hours", label: "Sleep (h)",          type: "number", step: 0.1 },
    { key: "note",        label: t("food_sheet.note"), type: "text" },
  ];

  const { data: payload, error: err, reload: load } = useCachedResource<{ measurements: Measurement[]; goals: Goals }>(
    "weight",
    async () => {
      const from = addDays(todayLocal(), -89);
      const [measurements, goals] = await Promise.all([
        api.listMeasurements(from, todayLocal()),
        api.getGoals(),
      ]);
      return { measurements, goals };
    },
    { maxAgeMs: WEIGHT_MAX_AGE_MS },
  );

  if (err && !payload) return <ErrorState message={t("error.load_failed")} onRetry={load} />;

  const rows = (payload?.measurements ?? []).slice().reverse();

  return (
    <div className="space-y-5 pt-4 animate-rise">
      <PageHeader back="/progress" title={t("weight.all_entries")} />

      {!payload ? (
        <div className="space-y-2">
          <Skeleton className="h-14 rounded-2xl" />
          <Skeleton className="h-14 rounded-2xl" />
          <Skeleton className="h-14 rounded-2xl" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-fg-faint">{t("weight.recent_empty")}</p>
      ) : (
        <div className="glass rounded-2xl px-4">
          {rows.map((m) => (
            <button
              key={m.id}
              onClick={() => setEditing(m)}
              className="w-full flex items-center justify-between gap-3 py-3.5 border-b border-line last:border-b-0 text-left press"
            >
              <div className="min-w-0">
                <div className="text-sm">{relativeDayLabel(m.measured_on, todayLocal(), t)}</div>
                <div className="text-sm text-fg-muted">{m.measured_on}</div>
              </div>
              <span className="tabular-nums text-right shrink-0">
                {m.weight_kg !== null ? units.weight(m.weight_kg) : "—"}
                {m.waist_cm !== null && (
                  <span className="text-fg-muted"> · {units.length(m.waist_cm)}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}

      <EditSheet<Measurement>
        open={editing !== null}
        title={editing?.measured_on ?? ""}
        fields={MEAS_FIELDS}
        initial={editing ?? {}}
        onSave={async (patch) => {
          if (editing) {
            await api.upsertMeasurement({ measured_on: editing.measured_on, ...patch });
            await load();
          }
        }}
        onDelete={async () => { if (editing) { await api.deleteMeasurement(editing.measured_on); await load(); } }}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}
